import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Typography, 
  Box,
  CircularProgress,
  Paper,
  Avatar,
  Chip
} from '@mui/material';
import { dashboardAPI, invoiceAPI, vendorAPI } from '../api/api';
import { useDashboard } from '../contexts/DashboardContext';
import StatsCard from '../components/StatsCard';
import CostOverviewChart from '../components/CostOverviewChart';
import VendorDistributionChart from '../components/VendorDistributionChart';
import MonthlyUsageByCategoryChart from '../components/MonthlyUsageByCategoryChart';
import StackedVendorCostChart from '../components/StackedVendorCostChart';
import { 
  Dashboard as DashboardIcon, 
  Receipt as ReceiptIcon, 
  TrendingUp as TrendingUpIcon, 
  LocalAtm as LocalAtmIcon,
  Business as BusinessIcon,
  Category as CategoryIcon
} from '@mui/icons-material';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [invoicesData, setInvoicesData] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const { dashboardRefreshTrigger } = useDashboard();

  const fetchYearlyData = async () => {
    try {
      await dashboardAPI.getCostGraph('yearly');
      // Process yearly data here if needed
    } catch (err) {
      console.error('Error fetching yearly data:', err);
    }
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsResponse, invoicesResponse, vendorsResponse] = await Promise.all([
          dashboardAPI.getStats(),
          invoiceAPI.getInvoices(0, 5), // Get 5 recent invoices
          vendorAPI.getVendors() // Get all vendors to map standard names
        ]);
        
        setStats(statsResponse.data);
        setInvoicesData(invoicesResponse.data || []);
        setVendors(vendorsResponse.data || []);
        
        // Fetch yearly data
        await fetchYearlyData();
      } catch (err) {
        // Handle different error response formats
        let errorMessage = 'Failed to load dashboard data';
        if (err.response?.data?.detail) {
          // Handle FastAPI validation errors which can be an array of objects
          if (Array.isArray(err.response.data.detail)) {
            errorMessage = err.response.data.detail.map(error => error.msg || error.detail || JSON.stringify(error)).join(', ');
          } else {
            errorMessage = err.response.data.detail;
          }
        } else if (err.response?.data && Array.isArray(err.response.data)) {
          errorMessage = err.response.data.map(error => error.msg || JSON.stringify(error)).join(', ');
        } else if (typeof err.response?.data === 'object' && err.response.data.msg) {
          errorMessage = err.response.data.msg;
        }
        
        setError(errorMessage);
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [dashboardRefreshTrigger]); // Add dashboardRefreshTrigger as a dependency

  // Function to get vendor name by ID, showing standard name if available
  const getVendorName = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    if (vendor) {
      // If the vendor has a standard_name, return it; otherwise return the name
      return vendor.standard_name && vendor.standard_name.trim() !== '' ? vendor.standard_name : vendor.name;
    }
    return 'N/A';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            mt: 4,
            textAlign: 'center',
            backgroundColor: 'background.paper',
            borderRadius: 3
          }}
        >
          <Typography color="error" variant="h6">
            {typeof error === 'string' 
              ? error 
              : Array.isArray(error) 
                ? error.map((err, idx) => <div key={idx}>{err.msg || JSON.stringify(err)}</div>) 
                : error.msg || error.detail || JSON.stringify(error) || 'An error occurred'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      {/* Dashboard Header */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
              Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Welcome back! Here's what's happening with your invoices today.
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <DashboardIcon />
            </Avatar>
          </Box>
        </Box>
      </Box>
      
      {/* Stats Cards Grid */}
      {stats && (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatsCard 
              title="Total Cost" 
              value={`${stats.total_cost !== undefined ? stats.total_cost.toFixed(2) : '0.00'}`} 
              change={`${stats.total_cost_change >= 0 ? '+' : ''}${stats.total_cost_change || 0}%`} 
              icon={<LocalAtmIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatsCard 
              title="This Month Bill" 
              value={`${stats.this_month_bill !== undefined ? stats.this_month_bill.toFixed(2) : '0.00'}`} 
              change={`${stats.this_month_bill_change >= 0 ? '+' : ''}${stats.this_month_bill_change || 0}%`} 
              icon={<TrendingUpIcon />}
              color="secondary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatsCard 
              title="Total Invoices" 
              value={stats.total_invoices || 0} 
              change={`${stats.total_invoices_change >= 0 ? '+' : ''}${stats.total_invoices_change || 0}%`} 
              icon={<ReceiptIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatsCard 
              title="Total Vendors" 
              value={stats.total_vendors || 0} 
              change={`${stats.total_vendors_change >= 0 ? '+' : ''}${stats.total_vendors_change || 0}%`} 
              icon={<BusinessIcon />}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatsCard 
              title="Total Categories" 
              value={stats.total_categories || 0} 
              change={`${stats.total_categories_change >= 0 ? '+' : ''}${stats.total_categories_change || 0}%`} 
              icon={<CategoryIcon />}
              color="info"
            />
          </Grid>
        </Grid>
      )}

      {/* Main Charts Grid */}
      <Grid container spacing={3} mb={3}>
        {/* Cost Overview Chart with Time Range Dropdown */}
        <Grid item xs={12} lg={4}>
          <CostOverviewChart />
        </Grid>
        
        {/* Vendor Distribution Chart */}
        <Grid item xs={12} lg={4}>
          <VendorDistributionChart />
        </Grid>
        
        {/* Monthly Usage by Category Chart */}
        <Grid item xs={12} lg={4}>
          <MonthlyUsageByCategoryChart />
        </Grid>
      </Grid>

      {/* Yearly Cost Chart - Updated to Stacked Vendor Chart */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12}>
          <StackedVendorCostChart />
        </Grid>
      </Grid>
      
      {/* Recent Invoices Section */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              backgroundColor: 'background.paper',
              borderRadius: 3,
              boxShadow: 1
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
              Recent Invoices
            </Typography>
            <Box>
              {invoicesData.length > 0 ? (
                invoicesData.map((invoice) => (
                  <Box key={invoice.id} display="flex" justifyContent="space-between" alignItems="center" py={1} borderBottom="1px solid" borderColor="divider">
                    <Box>
                      <Typography variant="body1" fontWeight="600">{getVendorName(invoice.vendor_id)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : 
                         invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="body1" fontWeight="600">${invoice.total_amount ? parseFloat(invoice.total_amount).toFixed(2) : '0.00'}</Typography>
                      <Chip 
                        label={invoice.payment_status || 'N/A'} 
                        size="small" 
                        sx={{ 
                          mt: 0.5,
                          backgroundColor: 
                            invoice.payment_status === 'Paid' ? 'success.light' : 
                            invoice.payment_status === 'Pending' ? 'warning.light' : 
                            'error.light',
                          color: 
                            invoice.payment_status === 'Paid' ? 'success.main' : 
                            invoice.payment_status === 'Pending' ? 'warning.main' : 
                            'error.main'
                        }} 
                      />
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No recent invoices
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;